import _objectDestructuringEmpty from "@babel/runtime/helpers/esm/objectDestructuringEmpty";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["client", "userEmail", "onClose", "formFields", "headerContents", "topNavBar", "topBodyContents", "bottomBodyContents", "onFailure", "onSuccess", "exportOptions", "emailFieldAndNotificationHelpText", "onFieldChange", "disableExportButton", "disabledExportButtonTooltipProps", "defaultFormat", "scrollHeight", "previousSteps", "previousStepsNextButtons", "nextSteps", "nextStepsNextButtons"];
import { useEffect, useState } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { Button } from 'foundations-components/transitional/components/Button';
import { Form } from 'foundations-components/transitional/components/Form';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { fetchPreferences } from '../apiClients/ExportPreferencesClient';
import { ScrollContainer } from 'foundations-components/transitional/components/ScrollContainer';
import { Text } from 'foundations-components/components/Text';
import { Wizard, WizardStep } from 'foundations-components/transitional/components/Wizard';
import { View } from 'foundations-components/components/View';
import { DefaultFieldKeys, Formats, ExportPreferences } from '../Constants';
import createFormField from './formFields/FormField';
import EmailField from './formFields/EmailField';
import FormatField from './formFields/FormatField';
import { getEmailValidationState, getExportReason, getFormFieldDefaults, trackCrmExportOptions } from './exportDialogUtilities';
import { ExportDialogEmailHelpText } from './ExportDialogEmailHelpText';
import { ExportDialogNotificationsOfModal } from './ExportDialogNotificationsOfModal';
import { UsageTracker } from '../../UsageTracker';
import { FloatingAlertList, FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
function ExportDialog(_ref) {
  let {
      client,
      userEmail,
      onClose,
      formFields = [createFormField(FormatField, DefaultFieldKeys.FORMAT), createFormField(EmailField, DefaultFieldKeys.EMAIL)],
      headerContents = /*#__PURE__*/_jsx(FormattedMessage, {
        message: "exportDialog.dialogHeading"
      }),
      topNavBar,
      topBodyContents,
      bottomBodyContents,
      onFailure,
      onSuccess,
      exportOptions = {},
      emailFieldAndNotificationHelpText,
      onFieldChange,
      disableExportButton = false,
      disabledExportButtonTooltipProps,
      defaultFormat,
      scrollHeight = 9999,
      // Default hack to 'hide' scroll container
      previousSteps,
      previousStepsNextButtons,
      nextSteps,
      nextStepsNextButtons
    } = _ref,
    otherProps = _objectWithoutPropertiesLoose(_ref, _excluded);
  const [exportPreferences, setExportPreferences] = useState({});
  const [isNotificationOff, setIsNotificationOff] = useState(false);
  const [preferenceState, setPreferenceState] = useState('NOT_REQUESTED');
  const [exportState, setExportState] = useState('NOT_REQUESTED');
  const [formState, setFormState] = useState(Object.assign({}, getFormFieldDefaults(formFields), {
    [DefaultFieldKeys.EMAIL]: userEmail,
    [DefaultFieldKeys.FORMAT]: defaultFormat || Formats.CSV
  }));
  const handlePreferenceFetch = loadedExportPreferences => {
    const {
      EXPORTS_GENERAL
    } = loadedExportPreferences.userPreferences.EXPORT;
    setExportPreferences(EXPORTS_GENERAL);
    setPreferenceState('SUCCEEDED');
    setIsNotificationOff(EXPORTS_GENERAL.EMAIL === ExportPreferences.DO_NOT_SEND && EXPORTS_GENERAL.NOTIFICATION_SIDEBAR === ExportPreferences.DO_NOT_SEND);
  };
  useEffect(() => {
    UsageTracker.track('pageview', {
      action: 'dialog opened'
    });
  }, []);
  useEffect(() => {
    if (preferenceState === 'NOT_REQUESTED') {
      fetchPreferences().then(handlePreferenceFetch).catch(err => console.error(err));
    }
  });
  const failExporting = (failureReason = 'UNKNOWN', xhr) => {
    setExportState('FAILED');
    if (failureReason === 'FREE_PORTAL_EXPORTS_LIMIT' || failureReason === 'PAID_PORTAL_EXPORTS_LIMIT') {
      try {
        const limit = JSON.parse(xhr.response).errorTokens.totalExportsLimit[0];
        FloatingAlertStore.addAlert({
          message: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "exportDialog.limitPortalAlertMessage",
            options: {
              limit
            }
          }),
          titleText: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "exportDialog.limitPortalAlertTitle"
          }),
          type: 'danger'
        });
      } catch (e) {
        // unable to determine limit
        FloatingAlertStore.addAlert({
          message: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "exportDialog.limitPortalAlertMessageUnknownLimit"
          }),
          titleText: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "exportDialog.limitPortalAlertTitle"
          }),
          type: 'danger'
        });
      }
      return;
    }
    if (failureReason === 'BLOCKED_PORTAL') {
      FloatingAlertStore.addAlert({
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "exportDialog.blockedPortalAlertMessage"
        }),
        titleText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "exportDialog.blockedPortalAlertTitle"
        }),
        type: 'danger'
      });
      return;
    }
    onClose(null);
    if (onFailure) {
      onFailure(failureReason, xhr);
    }
  };
  const isExportDisabled = exportState === 'PENDING' || exportState === 'FAILED';
  const exportFile = () => {
    const options = Object.assign({}, formState, exportOptions);
    trackCrmExportOptions(options);
    setExportState('PENDING');
    return client.export(options).then(() => {
      setExportState('SUCCEEDED');
      onClose(null);
      if (onSuccess) {
        onSuccess();
      }
    }, ({
      xhr = {}
    } = {}) => {
      const failureReason = getExportReason(xhr);
      return failExporting(failureReason, xhr);
    });
  };
  const startExport = () => {
    if (isExportDisabled) {
      return;
    }
    exportFile().catch(err => {
      console.error(err);
      throw err;
    });
  };
  const handleFieldChange = (key, value) => {
    const newFormState = Object.assign({}, formState, {
      [key]: value
    });
    setFormState(newFormState);
    if (onFieldChange) {
      // to mimic the old behavior of the formState object
      const get = itemKey => newFormState[itemKey];
      onFieldChange(Object.assign({}, newFormState, {
        get
      }));
    }
  };
  const renderFormFields = () => {
    return formFields.filter(formField => formField !== null && formField !== undefined).map((Field, index) => {
      const FieldAsFunction = Field;
      const FieldWithKey = Field;
      const value = formState[FieldWithKey.key];
      const additionalProps = {};
      if (FieldWithKey.key === DefaultFieldKeys.EMAIL) {
        additionalProps.validationState = getEmailValidationState(formState);
      }
      return /*#__PURE__*/_jsx(FieldAsFunction, Object.assign({
        value: value,
        onChange: handleFieldChange,
        preferences: exportPreferences
      }, additionalProps), FieldWithKey.key || index);
    });
  };
  const renderEmailFieldAndNotificationHelpText = () => {
    const Field = emailFieldAndNotificationHelpText;
    if (!Field) {
      return null;
    }
    const key = emailFieldAndNotificationHelpText.key || DefaultFieldKeys.EMAIL;
    const value = formState[key];
    const FieldComponent = Field;
    return /*#__PURE__*/_jsx(FieldComponent, {
      value: value,
      onChange: handleFieldChange,
      preferences: exportPreferences,
      validationState: getEmailValidationState(formState)
    }, key);
  };
  const renderCancelButton = () => /*#__PURE__*/_jsx(Button, {
    "data-test-id": "ExportDialog-cancel-button",
    "data-selenium-test": "ExportDialog-cancel-button",
    use: "secondary",
    onClick: e => {
      if (typeof onClose === 'function') {
        onClose(e);
      }
    },
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "exportDialog.cancel"
    })
  });
  const renderExportButton = (props = {}) => {
    const hasLastNextStepButton = nextStepsNextButtons && nextSteps && Boolean(nextStepsNextButtons[nextSteps.length - 1]);
    if (hasLastNextStepButton) {
      return nextStepsNextButtons[nextStepsNextButtons.length - 1](Object.assign({}, props, {
        onClick: startExport,
        loading: exportState === 'PENDING'
      }));
    }
    return /*#__PURE__*/_jsx(Tooltip, Object.assign({
      disabled: !disableExportButton || !disabledExportButtonTooltipProps
    }, disabledExportButtonTooltipProps, {
      children: /*#__PURE__*/_jsx(LoadingButton, {
        "data-test-id": "ExportDialog-submit-button",
        "data-selenium-test": "ExportDialog-submit-button",
        use: "primary",
        onClick: startExport,
        loading: exportState === 'PENDING',
        disabled: getEmailValidationState(formState) !== 'VALID' || isExportDisabled || disableExportButton,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "exportDialog.exportCta"
        })
      })
    }));
  };
  const renderExportStepButton = _ref2 => {
    let props = Object.assign({}, (_objectDestructuringEmpty(_ref2), _ref2));
    return /*#__PURE__*/_jsx(Tooltip, Object.assign({
      disabled: !disableExportButton || disabledExportButtonTooltipProps === null
    }, disabledExportButtonTooltipProps, {
      children: /*#__PURE__*/_jsx(LoadingButton, Object.assign({}, props, {
        "data-test-id": "ExportDialog-submit-button",
        "data-selenium-test": "ExportDialog-submit-button",
        use: "primary",
        loading: exportState === 'PENDING',
        disabled: getEmailValidationState(formState) !== 'VALID' || isExportDisabled || disableExportButton
      }))
    }));
  };
  const renderModal = ({
    withoutContainer
  } = {
    withoutContainer: false
  }) => {
    if (withoutContainer) {
      return /*#__PURE__*/_jsxs(View, {
        "data-test-id": "modal-container",
        children: [topNavBar, emailFieldAndNotificationHelpText && renderEmailFieldAndNotificationHelpText(), topBodyContents, /*#__PURE__*/_jsx(View, {
          children: /*#__PURE__*/_jsx(Form, {
            children: renderFormFields()
          })
        }), bottomBodyContents, !emailFieldAndNotificationHelpText && /*#__PURE__*/_jsx(ExportDialogEmailHelpText, {})]
      });
    }
    return /*#__PURE__*/_jsx(View, {
      "data-test-id": "modal-container",
      children: /*#__PURE__*/_jsx(ScrollContainer, {
        maxHeight: scrollHeight,
        children: /*#__PURE__*/_jsxs(DialogBody, {
          children: [topNavBar, emailFieldAndNotificationHelpText && renderEmailFieldAndNotificationHelpText(), topBodyContents, /*#__PURE__*/_jsx(View, {
            children: /*#__PURE__*/_jsx(Form, {
              children: renderFormFields()
            })
          }), bottomBodyContents, !emailFieldAndNotificationHelpText && /*#__PURE__*/_jsx(ExportDialogEmailHelpText, {})]
        })
      })
    });
  };
  const getBody = () => {
    const loading = preferenceState !== 'SUCCEEDED' || exportPreferences == null;
    if (loading) {
      return /*#__PURE__*/_jsx(DialogBody, {
        children: /*#__PURE__*/_jsx(LoadingSpinner, {
          grow: true,
          minHeight: 200,
          layout: "centered"
        })
      });
    }
    if (isNotificationOff) {
      return /*#__PURE__*/_jsx(DialogBody, {
        children: /*#__PURE__*/_jsx(ExportDialogNotificationsOfModal, {
          onClose: onClose
        })
      });
    }
    if (previousSteps || nextSteps) {
      return /*#__PURE__*/_jsxs(Wizard, {
        onConfirm: () => onClose(null),
        onReject: () => onClose(null),
        defaultDoneButton: renderExportButton,
        headerComponent: null,
        children: [(previousSteps || []).map((step, index) => /*#__PURE__*/_jsx(WizardStep, {
          nextButton: previousStepsNextButtons && previousStepsNextButtons[index],
          children: typeof step === 'function' ? step({
            formState
          }) : step
        }, index)), /*#__PURE__*/_jsx(WizardStep, {
          nextButton: renderExportStepButton,
          children: renderModal({
            withoutContainer: true
          })
        }), (nextSteps || []).map((step, index) => /*#__PURE__*/_jsx(WizardStep, {
          nextButton: nextStepsNextButtons && nextStepsNextButtons[index],
          children: typeof step === 'function' ? step({
            formState
          }) : step
        }, index))]
      });
    }
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [renderModal(), /*#__PURE__*/_jsxs(DialogFooter, {
        children: [renderExportButton(), renderCancelButton()]
      })]
    });
  };
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(FloatingAlertList, {}), /*#__PURE__*/_jsxs(Modal, Object.assign({
      "data-test-id": "ExportDialog-modal",
      "data-selenium-test": "export-dialog"
    }, otherProps, {
      children: [/*#__PURE__*/_jsxs(DialogHeader, {
        children: [/*#__PURE__*/_jsx(DialogCloseButton, {
          onClick: e => {
            if (typeof onClose === 'function') {
              onClose(e);
            }
          }
        }), /*#__PURE__*/_jsx(Text, {
          as: "h2",
          children: headerContents
        })]
      }), getBody()]
    }))]
  });
}
export default ExportDialog;